/// <reference types="google.maps" />
import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { Address, AddressComponent } from '../../api/agwa-api-model';
import { getClientConfig } from '../../api/client-config-provider';
import {
  configureGoogle,
  importLibrary,
} from '../../utility/maps/google-maps-loader';
import { COUNTRIES, findCountryByCode } from './countries';
import '@shoelace-style/shoelace/dist/components/tooltip/tooltip.js';
import {
  addressLines,
  composeLocality,
  composeStreet,
  isRedundantPlaceName,
  singleLine,
} from './address-format';
import {
  autoAdoptDecision,
  coordsPatch,
  proposalPatch,
  type AddressProposal,
  type AddressTextParts,
} from './address-transitions';
import { terms } from '../../language/terms';

const AU_CENTER = { lat: -25.3, lng: 133.8 };

/**
 * Edit surface for a site address. The job of this screen is not "fill in an address form" - it is
 * **confirm that this site's location is real and routable**, because everything downstream (auditor
 * routing, mapping) needs a point, not a string.
 *
 * That is why the two states look different rather than the same:
 *
 * - **Resolved** (has a pin): the address is rendered as the *answer* - readable lines plus a map -
 *   with the structured fields tucked into a disclosure. When Google resolved the address those
 *   fields are derived output, and showing them as seven inputs implies an edit that does nothing:
 *   changing "Suburb" moves no pin and re-resolves nothing. It also reads as broken for a landmark,
 *   where Google legitimately returns no street number and no route.
 * - **Manual entry** ("Enter manually"): the structured fields *are* the input - the way a person
 *   builds something geocodable - so they are shown in full, with "Find on map" to place the pin.
 *   In "Find on Google" the search box is the input instead, so the structured fields stay hidden
 *   there (they appear only to refine a resolved result) - the two modes never both show a form.
 *
 * Renders in LIGHT DOM so the Places autocomplete element behaves and the field CSS applies.
 * Styling uses Shoelace design tokens throughout, so the surface themes with the rest of the app
 * in both light and dark rather than carrying hardcoded colours.
 *
 * Emits `agwa-address-change` (bubbles, composed) with the working {@link Address} on every change.
 * Save / cancel and the required-location gate are owned by the host.
 *
 * @element agwa-site-address-edit
 * @fires agwa-address-change - Fired when the address or location changes.
 */
@customElement('agwa-site-address-edit')
export class AgwaSiteAddressEdit extends LitElement {
  /** The working address (host-owned). */
  @property({ attribute: false }) address: Address | null = null;

  /**
   * The address currently saved against the site, shown for reference. Kept separate from the
   * working address so the screen can say "this is what it is now" without that claim drifting as
   * the user works.
   */
  @property({ attribute: false }) currentAddress: Address | null = null;

  /**
   * Which path the user is on. Explicit rather than inferred, because the two are genuinely
   * different jobs:
   *
   * - `search` - let Google find the place, then refine the detail.
   * - `manual` - type the address, geocode it as close as Google can get, then place the pin.
   *
   * Both end in the same place: a readable address with a pin on the map.
   */
  @state() private mode: 'search' | 'manual' = 'search';

  /** In search mode, whether the written detail is open for refinement. */
  @state() private refiningDetail = false;

  /**
   * Where the manual path has got to. Typing invalidates the pin, because a pin that no longer
   * matches the words is worse than no pin - it looks confirmed while pointing somewhere else.
   *
   * - `entering`     - typed, not yet located. No pin.
   * - `approximate`  - Google got close but not to a building. The pin MUST be placed by hand.
   * - `exact`        - Google matched a rooftop. The pin is good; refining is optional.
   */
  @state() private manualStage: 'entering' | 'approximate' | 'exact' | null =
    null;

  /** Set once the user actually moves the pin, so "refine this" can stop nagging when they have. */
  @state() private pinAdjusted = false;

  /** Outcome of the attempt to locate the saved address on the map. */
  @state() private autoResolve: 'searching' | 'found' | 'notfound' | null =
    null;

  /** The address only resolved once the place name was dropped - the two disagree. */
  @state() private resolvedWithoutName = false;

  /** Google's detail was adopted automatically because it was strictly more complete. */
  @state() private detailsAutoFilled = false;

  /**
   * What Google says is at the pin, offered rather than applied. Silently overwriting the address
   * someone is in the middle of typing would fight them; showing it and letting them take it is
   * the same information without the ambush.
   */
  @state() private pinCandidate: AddressProposal | null = null;
  @state() private status = '';
  @state() private mapNote = '';
  @state() private configError = '';
  @state() private mapLoading = true;
  @state() private hasKey = true;

  private map?: google.maps.Map;
  private marker?: google.maps.marker.AdvancedMarkerElement;
  private geocoder?: google.maps.Geocoder;
  private autocomplete?: google.maps.places.PlaceAutocompleteElement;
  private mapId = '';
  private initialised = false;
  private reverseGeocodeToken = 0;

  /**
   * Guards every path that establishes a location. Without it a slow auto-locate can land after
   * the user has picked a different place and silently drag the pin back.
   */
  private resolveToken = 0;

  // Light DOM: the Places autocomplete element behaves better outside a shadow root, and the
  // field-grid CSS needs to apply to it.
  protected override createRenderRoot(): HTMLElement {
    return this;
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.teardownMap();
  }

  protected override firstUpdated(): void {
    void this.initGoogle();
  }

  // ---- Derived state ---------------------------------------------------------------------------

  /** A pin is the whole test for whether this address is usable - see the design doc. */
  private get isResolved(): boolean {
    return this.lat != null && this.lng != null;
  }

  /** True once the working address differs from what is saved. */
  private get hasChanged(): boolean {
    const current = this.currentAddress;
    return (
      singleLine(this.address) !== singleLine(current) ||
      this.lat !==
        (typeof current?.latitude === 'number' ? current.latitude : null) ||
      this.lng !==
        (typeof current?.longitude === 'number' ? current.longitude : null)
    );
  }

  /**
   * When the structured fields are shown. They are manual entry's whole surface, so manual mode
   * always shows them. In "Find on Google" they exist ONLY to refine an already-resolved result -
   * they must not appear for an unresolved address, because the Google search box is the input there.
   * Showing the empty manual grid as well (the state left behind after toggling manual -> Find on
   * Google, which clears the pin) duplicates the finder with a form the search flow never uses.
   */
  private get showFields(): boolean {
    if (this.mode === 'manual') return true;
    return this.refiningDetail;
  }

  private get lat(): number | null {
    const v: unknown = this.address?.latitude;
    return typeof v === 'number' ? v : null;
  }

  private get lng(): number | null {
    const v: unknown = this.address?.longitude;
    return typeof v === 'number' ? v : null;
  }

  private val(key: keyof Address): string {
    const v: unknown = this.address?.[key];
    return typeof v === 'string' ? v : '';
  }

  // ---- Address composition -----------------------------------------------------------------
  // Shared with the read-only panel and mirroring Address.ToDisplayString on the server, so the
  // same site reads identically everywhere. See ./address-format.ts.

  private composeStreet(): string {
    return composeStreet(this.address);
  }

  private composeLocality(): string {
    return composeLocality(this.address);
  }

  /** The readable address, as display lines. */
  private addressLines(): string[] {
    return addressLines(this.address);
  }

  /**
   * A link that proves the address routes. Prefers the place id, which pins Google to the exact
   * place rather than re-resolving the coordinates.
   */
  private mapsUrl(): string | null {
    const lat = this.lat;
    const lng = this.lng;
    if (lat == null || lng == null) return null;
    const placeId = this.val('googlePlaceId');
    const query = `${lat},${lng}`;
    return placeId
      ? `https://www.google.com/maps/search/?api=1&query=${query}&query_place_id=${encodeURIComponent(placeId)}`
      : `https://www.google.com/maps/search/?api=1&query=${query}`;
  }

  // ---- Render ----------------------------------------------------------------------------------

  protected override render() {
    return html`
      ${this.styleTemplate()}
      <div class="addr-grid">
        <div class="addr-col">
          ${this.currentTemplate()} ${this.modeTemplate()}
          <!-- Always rendered, hidden in manual mode. Google's autocomplete is a stateful
               third-party element: removing it from the DOM destroys it, and initAutocomplete
               will not rebuild one because it already holds a reference - so conditional
               rendering silently loses the search box on the way back to this mode. -->
          <div
            class="addr-lookup ${this.mode === 'manual' ? 'is-hidden' : ''}"
            ?aria-hidden=${this.mode === 'manual'}
          >
            ${this.lookupTemplate()}
          </div>
          ${this.summaryTemplate()}
          ${
            this.mode === 'search' && this.isResolved && !this.refiningDetail
              ? this.unitTemplate()
              : nothing
          }
          ${this.showFields ? this.fieldsTemplate() : nothing}
          ${this.statusTemplate()}
          ${
            this.status
              ? html`<p class="addr-note addr-note-warn">${this.status}</p>`
              : nothing
          }
        </div>

        <div class="addr-col">${this.mapTemplate()}</div>
      </div>
    `;
  }

  /**
   * What the site's address is today. Read-only and stated once, so everything below it can be
   * about entering a new one rather than about interpreting a half-edited state.
   */
  private currentTemplate() {
    const current = singleLine(this.currentAddress);
    if (!current) return nothing;

    return html`
      <div class="addr-current">
        <span class="addr-current-head">
          <span class="addr-current-label">Current address</span>
          ${this.currentBadgeTemplate()}
        </span>
        <span class="addr-current-value">${current}</span>
        ${
          this.autoResolve === null || this.autoResolve === 'notfound'
            ? html`<button
                type="button"
                class="addr-link"
                @click=${this.searchForCurrent}
              >
                Search for this address
              </button>`
            : nothing
        }
      </div>
    `;
  }

  /**
   * Whether the saved address can actually be used. The test is the pin and only the pin: without
   * coordinates nothing can route to this site, however complete the wording looks.
   */
  private currentBadgeTemplate() {
    const verified =
      typeof this.currentAddress?.latitude === 'number' &&
      typeof this.currentAddress?.longitude === 'number';

    return verified
      ? html`<span
          class="addr-badge verified"
          title="This ${terms.loginSite.lower} has a map location"
          >Verified</span
        >`
      : html`<span
          class="addr-badge uncertain"
          title="This ${terms.loginSite.lower} has no map location, so it cannot be routed to"
          >Uncertain</span
        >`;
  }

  /**
   * Seeds the lookup with the saved address. Confirming an existing address is the common job -
   * every one of the sites being cleaned up already has one - and this keeps that a single click
   * without the finder pretending to be a display of the current value.
   */
  private searchForCurrent(): void {
    this.setMode('search');
    void this.locateCurrentAddress();
  }

  /**
   * The single statement about the working address (rule R8).
   *
   * "Is there a pin", "how was it found" and "how precise is it" used to be answered in four
   * different places at once - a badge, a banner, a provenance caption and a stage note - so a
   * correct address could read as VERIFIED, MATCHED TO AN ADDRESS and "approximate match" at the
   * same time. Three true statements arranged to look like a contradiction. This says it once.
   */
  private statusTemplate() {
    const note = html`${
      this.detailsAutoFilled
        ? html` The written address was updated from Google.`
        : nothing
    }${
      this.resolvedWithoutName
        ? html` It only matched once the place name was ignored, so the name and
          the street address may not belong together.`
        : nothing
    }`;

    if (this.autoResolve === 'searching') {
      return html`<p class="addr-stage">Looking for the current address...</p>`;
    }

    if (this.autoResolve === 'notfound') {
      return html`<p class="addr-stage addr-stage-todo">
        That address could not be found on the map. Search for it above, or
        switch to <strong>Enter manually</strong> and place the pin yourself.
      </p>`;
    }

    if (!this.isResolved) {
      return html`<p class="addr-stage addr-stage-todo">
        No location set. This ${terms.loginSite.lower} cannot be routed to until
        it has a pin.
      </p>`;
    }

    if (this.pinAdjusted) {
      return html`<p class="addr-stage addr-stage-ok">
        Pin placed by hand. This is where the ${terms.loginSite.lower} will be
        routed to.${note}
      </p>`;
    }

    if (this.manualStage === 'approximate') {
      return html`<p class="addr-stage addr-stage-todo">
        Approximate match - Google could not find the exact building.
        <strong>Drag the pin to the ${terms.loginSite.lower} entrance</strong>
        before saving.${note}
      </p>`;
    }

    return html`<p class="addr-stage addr-stage-ok">
      Exact location found. Refine the pin if it is not where vehicles should
      arrive.${note}
    </p>`;
  }

  private modeTemplate() {
    const tab = (value: 'search' | 'manual', label: string) => html`
      <button
        type="button"
        class="addr-tab ${this.mode === value ? 'active' : ''}"
        aria-pressed=${this.mode === value ? 'true' : 'false'}
        @click=${() => this.setMode(value)}
      >
        ${label}
      </button>
    `;

    return html`
      <div class="addr-mode-row">
        <div
          class="addr-modes"
          role="group"
          aria-label="How to set this address"
        >
          ${tab('search', 'Find on Google')} ${tab('manual', 'Enter manually')}
        </div>
        ${this.helpTemplate()}
      </div>
    `;
  }

  /**
   * Explains why an address can look incomplete and still be right. Without this, a landmark that
   * legitimately has no street number reads as missing data, which is the single most common
   * misreading of this screen.
   *
   * Click as well as hover, so it works on touch and by keyboard rather than only under a mouse.
   */
  private helpTemplate() {
    return html`
      <sl-tooltip trigger="hover focus click" hoist>
        <div slot="content" class="addr-help">
          <p>
            ${terms.loginSite.value} addresses are matched with Google Maps so
            every ${terms.loginSite.lower} has an exact location that can be
            routed to. Entering one by hand is a backup for places Google cannot
            find.
          </p>
          <p>
            Because addresses come from Google, they do not always fill every
            field. A business or landmark often has no street number, and
            sometimes no suburb. That is expected - the pin on the map is what
            the ${terms.loginSite.lower} is located by, not the wording.
          </p>
        </div>
        <button
          type="button"
          class="addr-help-btn"
          aria-label="About ${terms.loginSite.lower} addresses"
        >
          ?
        </button>
      </sl-tooltip>
    `;
  }

  private setMode(mode: 'search' | 'manual'): void {
    const leavingSearch = this.mode === 'search' && mode === 'manual';
    this.mode = mode;
    this.refiningDetail = false;
    this.status = '';
    this.pinCandidate = null;

    // Choosing to type the address means starting from nothing. Carrying a previous lookup's
    // identity into manual entry is how you end up with "Syndal Baptist Church" sitting above a
    // completely different address. Cancel on the host form is the undo for a mis-click.
    if (leavingSearch) {
      this.manualStage = 'entering';
      this.pinAdjusted = false;
      this.clearPin();
      this.address = {
        ...({} as Address),
        source: 'Manual',
        components: [],
      };
      this.emitChange();
      this.clearFinder();
    }
  }

  private lookupTemplate() {
    if (!this.hasKey) {
      return html`<div class="addr-note addr-note-warn">
        Address lookup is unavailable - map configuration is missing. Enter the
        address below and drop a pin on the map.
      </div>`;
    }
    return html`
      <label class="addr-label" for="addr-autocomplete-slot">
        Find this address
      </label>
      <div id="addr-autocomplete-slot"></div>
      <p class="addr-hint">
        Search for the business, building or street address. Selecting a result
        sets the location.
      </p>
    `;
  }

  private summaryTemplate() {
    const lines = this.addressLines();
    if (lines.length === 0) return nothing;

    // Nothing has been changed yet, so this would just restate the current-address line above it.
    if (!this.hasChanged) return nothing;

    return html`
      <div class="addr-summary">
        <div class="addr-summary-lines">
          ${lines.map(
            (line, i) =>
              html`<span class=${i === 0 ? 'addr-line-lead' : 'addr-line'}
                >${line}</span
              >`
          )}
        </div>
        <div class="addr-summary-foot">
          ${
            this.mode === 'search' && this.isResolved
              ? html`<button
                  type="button"
                  class="addr-link"
                  @click=${() => (this.refiningDetail = !this.refiningDetail)}
                  aria-expanded=${this.refiningDetail ? 'true' : 'false'}
                >
                  ${this.refiningDetail ? 'Done' : 'Refine detail'}
                </button>`
              : nothing
          }
        </div>
      </div>
    `;
  }

  /**
   * The only part of a resolved address a person legitimately adds. Google resolves the building;
   * which unit inside it the member occupies is local knowledge it will never return, and AGWA's
   * membership is full of factories and workshops in multi-tenant industrial estates.
   */
  private unitTemplate() {
    // autocomplete="off" on the unit field for the same reason as field() below: a saved address
    // is the person's, never the site's.
    return html`
      <div class="addr-unit">
        <div class="addr-field">
          <label for="addr-unit-only">Unit or level</label>
          <input
            id="addr-unit-only"
            type="text"
            autocomplete="off"
            .value=${this.val('unitNumber')}
            @input=${(e: Event) =>
              this.setField('unitNumber', (e.target as HTMLInputElement).value)}
          />
        </div>
        <p class="addr-hint">
          Optional. Add this if Google matched the whole building and the
          ${terms.loginSite.lower} is one unit inside it.
        </p>
      </div>
    `;
  }

  private fieldsTemplate() {
    return html`
      ${
        this.mode === 'manual'
          ? html`<p class="addr-hint addr-manual-lead">
              Type the address, then place the pin. The pin is what the
              ${terms.loginSite.lower} is routed to, so it matters more than the
              wording.
            </p>`
          : nothing
      }
      <div class="addr-fields">
        ${this.field('unitNumber', 'Unit', 'span-4')}
        ${this.field('streetNumber', 'Street number', 'span-4')}
        ${this.field('streetName', 'Street name', 'span-8')}
        ${this.field('suburb', 'Suburb / city', 'span-6')}
        ${this.field('state', 'State', 'span-3')}
        ${this.field('postcode', 'Postcode', 'span-3')}
        <div class="addr-field span-12">
          <label for="addr-country">Country</label>
          <select
            id="addr-country"
            .value=${this.address?.countryCode?.toUpperCase() ?? ''}
            @change=${(e: Event) =>
              this.setCountry((e.target as HTMLSelectElement).value)}
          >
            <option value="">Select country</option>
            ${COUNTRIES.map(
              (c) =>
                html`<option
                  value=${c.code}
                  ?selected=${
                    c.code === this.address?.countryCode?.toUpperCase()
                  }
                >
                  ${c.name}
                </option>`
            )}
          </select>
        </div>
      </div>

      <div class="addr-actions">
        <button type="button" class="addr-btn" @click=${this.findOnMap}>
          ${this.mode === 'manual' ? 'Resolve on map' : 'Find on map'}
        </button>
        <span class="addr-hint">
          ${
            this.mode === 'manual'
              ? 'Locates the address as precisely as Google can from what you typed.'
              : 'Typing here changes the written address only. Use Find on map to move the pin to match it.'
          }
        </span>
      </div>
    `;
  }

  private mapTemplate() {
    const mapsUrl = this.mapsUrl();
    return html`
      <div class="addr-map-head">
        <span class="addr-label">Location</span>
        ${
          mapsUrl
            ? html`<a
                class="addr-link"
                href=${mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                >Open in Google Maps</a
              >`
            : nothing
        }
      </div>
      <div class="addr-map-wrap">
        <!-- #addr-map is owned by Google Maps and must contain NO Lit expressions: Google injects
             DOM here, which ejects Lit's marker and makes every later render throw. -->
        <div id="addr-map" class="addr-map"></div>
        ${
          this.mapLoading
            ? html`<div class="addr-map-status">
                ${this.configError || 'Loading map...'}
              </div>`
            : nothing
        }
      </div>
      <p class="addr-hint">
        ${
          !this.isResolved && this.mode === 'manual'
            ? 'Use Find on map, or click the map to place the pin yourself.'
            : 'Click the map or drag the pin to adjust.'
        }
      </p>
      ${this.mapNote ? html`<p class="addr-note">${this.mapNote}</p>` : nothing}
      ${this.pinCandidateTemplate()}
      ${
        this.isResolved
          ? html`<div class="addr-coords">
              <span>${this.lat?.toFixed(6)}, ${this.lng?.toFixed(6)}</span>
            </div>`
          : nothing
      }
    `;
  }

  /**
   * No placeholder text. Grey "e.g. 42" inside an empty input reads as data at a glance, which is
   * exactly the wrong signal on a screen whose job is telling you whether an address is there. The
   * label already says what the field is.
   */
  /**
   * What Google says is at the pin. Shown after the pin is moved, because the address under a
   * hand-placed pin is the most direct evidence of whether it landed where the user meant. Offered
   * rather than applied - silently overwriting an address someone is typing would fight them.
   */
  private pinCandidateTemplate() {
    const candidate = this.pinCandidate;
    if (!candidate) return nothing;

    // Always offered. It used to hide itself once the formatted address matched, which was wrong:
    // resolving sets the formatted address but not the structured components, so the action that
    // would fill in the empty state, postcode and country disappeared exactly when it was needed.
    // Applying twice is harmless.
    return html`
      <div class="addr-pin-found">
        <span class="addr-pin-found-label">
          Google's address for this location
        </span>
        <span class="addr-pin-found-value">${candidate.formattedAddress}</span>
        <button
          type="button"
          class="addr-link"
          @click=${() => this.acceptPinCandidate()}
        >
          Use these details
        </button>
      </div>
    `;
  }

  private field(key: keyof Address, label: string, span: string) {
    const id = `addr-${String(key)}`;
    // autocomplete="off": these are exactly the fields a browser's saved-address autofill targets,
    // and a site's address is never the person's own, so an offered one is always the wrong one
    // (data-entry-autofill.md). A raw input is outside the sl-input initializer's reach.
    return html`<div class="addr-field ${span}">
      <label for=${id}>${label}</label>
      <input
        id=${id}
        type="text"
        autocomplete="off"
        .value=${this.val(key)}
        @input=${(e: Event) =>
          this.setField(key, (e.target as HTMLInputElement).value)}
      />
    </div>`;
  }

  // ---- Field edits -----------------------------------------------------------------------------

  private patch(changes: Partial<Address>): void {
    this.address = { ...(this.address ?? ({} as Address)), ...changes };
    this.emitChange();
  }

  /**
   * Correcting a structured field does not change `source` or the pin: source records how the
   * *location* was resolved, which is still true. A member in unit 4 of an industrial estate is
   * local knowledge Google will not return, and capturing it should not discard a good pin.
   */
  private setField(key: keyof Address, value: string): void {
    // In search mode the written detail is a refinement of a place Google already located, so it
    // must not disturb the pin. In manual mode the words *are* the address, so changing them
    // invalidates any location derived from the old ones.
    this.clearFinder();

    if (this.mode === 'manual' && this.isResolved) {
      this.manualStage = 'entering';
      this.pinAdjusted = false;
      this.clearPin();
      this.patch({ [key]: value || undefined, source: 'Manual' });
      return;
    }

    this.patch({ [key]: value || undefined });
  }

  /**
   * The lookup box is for searching, not for reporting what the address currently is. It is seeded
   * once on open so an existing address is a confirm-and-save, but the moment the address changes
   * by any other route - a moved pin, an adopted pin address, typing - its contents describe
   * something that is no longer true, so it goes.
   */
  /**
   * Clears everything derived from a previous resolution attempt. Any action that establishes a new
   * location must call this, or the interface keeps asserting things about the old one - a pin
   * Google just placed still described as "placed by hand", or an offer to adopt the address of a
   * location the user has already moved away from.
   */
  private resetResolutionState(): void {
    this.autoResolve = null;
    this.manualStage = null;
    this.pinAdjusted = false;
    this.detailsAutoFilled = false;
    this.resolvedWithoutName = false;
    this.pinCandidate = null;
    this.status = '';
    this.mapNote = '';
  }

  private clearFinder(): void {
    if (this.autocomplete) this.autocomplete.value = '';
  }

  private clearPin(): void {
    if (this.marker) this.marker.map = null;
    this.marker = undefined;
    this.address = {
      ...(this.address ?? ({} as Address)),
      latitude: undefined,
      longitude: undefined,
      // No pin means no one placed one.
      pinSource: undefined,
    };
  }

  private setCountry(code: string): void {
    this.clearFinder();
    const country = findCountryByCode(code);
    this.patch({ country: country?.name, countryCode: country?.code });
  }

  // ---- Google ----------------------------------------------------------------------------------

  private async initGoogle(): Promise<void> {
    if (this.initialised) return;
    this.initialised = true;

    let key: string;
    try {
      const config = await getClientConfig();
      key = config.googleMapsBrowserKey;
      this.mapId = config.googleMapId;
    } catch {
      this.hasKey = false;
      this.configError = 'Map configuration unavailable.';
      this.mapLoading = false;
      return;
    }

    if (!key || !this.mapId) {
      this.hasKey = false;
      this.configError = 'Map configuration is incomplete.';
      this.mapLoading = false;
      return;
    }

    configureGoogle(key);
    try {
      await Promise.all([
        importLibrary('maps'),
        importLibrary('marker'),
        importLibrary('places'),
        importLibrary('geocoding'),
      ]);
    } catch {
      this.configError =
        'Could not load Google Maps. Ensure the Maps JavaScript and Places APIs are enabled.';
      this.mapLoading = false;
      return;
    }

    if (!this.isConnected) return; // torn down while libraries loaded
    this.mapLoading = false;
    await this.updateComplete;
    this.initMap();
    this.initAutocomplete();
    if (!this.isResolved) void this.locateCurrentAddress();
  }

  /**
   * Locates the saved address on the map.
   *
   * Runs automatically when an existing address has never been pinned - every site captured before
   * this feature - so the cleanup becomes check-and-save rather than search-and-save. Also runs on
   * request from the current-address line. Nothing is persisted; the host still owns Save/Cancel.
   */
  private async locateCurrentAddress(): Promise<void> {
    if (!this.map) return;

    const queries = this.locateQueries();
    if (queries.length === 0) return;

    const token = ++this.resolveToken;
    this.resetResolutionState();
    this.autoResolve = 'searching';
    if (this.autocomplete) this.autocomplete.value = queries[0];

    const best = await this.geocodeBest(queries);
    if (!this.isConnected || token !== this.resolveToken) return;

    if (!best) {
      this.autoResolve = 'notfound';
      return;
    }

    // No `source` change: locating an address only establishes where it is. The written address
    // is still the one the user holds until they adopt Google's, which is what marks it as
    // Google's answer.
    this.patch({
      googlePlaceId: best.result.place_id ?? undefined,
      formattedAddress: best.result.formatted_address ?? undefined,
    });
    const loc = best.result.geometry.location;
    this.setCoords(loc.lat(), loc.lng(), 'google');
    this.map.panTo({ lat: loc.lat(), lng: loc.lng() });
    this.map.setZoom(this.zoomForPrecision(best.result.geometry.location_type));
    this.manualStage =
      best.result.geometry.location_type === 'ROOFTOP'
        ? 'exact'
        : 'approximate';
    this.pinAdjusted = false;
    this.resolvedWithoutName = best.index > 0;
    this.autoResolve = 'found';
    // The point of locating an existing address is to improve it. Without this the primary
    // clean-up journey gets a pin and keeps its sparse, lowercase wording.
    this.proposeFromResult(best.result, this.resolvedWithoutName);
  }

  /**
   * What to try, best guess first. The second query drops the place name, because a name that does
   * not belong to the address defeats the whole query - "Syndal Baptist Church, 25 Harbour St,
   * Sydney" finds nothing, while "25 Harbour St, Sydney" resolves exactly. The street address is
   * the part we can trust when the two disagree.
   */
  private locateQueries(): string[] {
    const current = this.currentAddress;
    const withName = singleLine(current);
    const withoutName = [
      composeStreet(current),
      composeLocality(current),
      typeof current?.country === 'string' ? current.country : '',
    ]
      .filter(Boolean)
      .join(', ');

    const queries = [withName, withoutName].filter(Boolean);
    return [...new Set(queries)];
  }

  /**
   * Geocodes each query and keeps the most precise answer rather than the first one. A query can
   * succeed while landing on a suburb centroid, and a later query can do better.
   */
  private async geocodeBest(
    queries: string[]
  ): Promise<{ result: google.maps.GeocoderResult; index: number } | null> {
    const rank = (type: string): number =>
      ({
        ROOFTOP: 4,
        RANGE_INTERPOLATED: 3,
        GEOMETRIC_CENTER: 2,
        APPROXIMATE: 1,
      })[type] ?? 0;

    this.geocoder ??= new google.maps.Geocoder();
    let best: { result: google.maps.GeocoderResult; index: number } | null =
      null;

    for (const [index, address] of queries.entries()) {
      try {
        const { results } = await this.geocoder.geocode({
          address,
          region: this.regionCode(),
        });
        const candidate = results[0];
        if (!candidate) continue;

        if (
          !best ||
          rank(candidate.geometry.location_type) >
            rank(best.result.geometry.location_type)
        ) {
          best = { result: candidate, index };
        }

        // Nothing beats a rooftop match, so stop paying for further lookups.
        if (candidate.geometry.location_type === 'ROOFTOP') break;
      } catch {
        // A failed query is just one that did not answer; try the next.
      }
    }

    return best;
  }

  private initMap(): void {
    const el = this.querySelector<HTMLDivElement>('#addr-map');
    if (!el) return;
    const lat = this.lat;
    const lng = this.lng;
    const center = lat != null && lng != null ? { lat, lng } : AU_CENTER;
    this.map = new google.maps.Map(el, {
      center,
      zoom: lat != null ? 17 : 4,
      mapId: this.mapId,
      streetViewControl: false,
      mapTypeControl: false,
      fullscreenControl: false,
      clickableIcons: false,
    });
    this.map.addListener('click', (e: google.maps.MapMouseEvent) => {
      if (e.latLng) this.setCoords(e.latLng.lat(), e.latLng.lng(), 'manual');
    });
    if (lat != null && lng != null) this.ensureMarker(lat, lng);
  }

  private initAutocomplete(): void {
    if (this.autocomplete) return;
    const slot = this.querySelector('#addr-autocomplete-slot');
    if (!slot) return;

    // No includedPrimaryTypes restriction: a site locator must find businesses and landmarks, not
    // just street addresses. Region follows the site's own country - production is not all
    // Australian (2 NZ sites, 1 in China), and biasing those to AU returns plausible-wrong results.
    const el = new google.maps.places.PlaceAutocompleteElement({
      includedRegionCodes: [this.regionCode()],
    });
    el.style.width = '100%';

    slot.appendChild(el);
    el.addEventListener('gmp-select', (e) => void this.onSelect(e));
    this.autocomplete = el;
  }

  private regionCode(): string {
    const code = this.val('countryCode').toLowerCase();
    return code || 'au';
  }

  private async onSelect(e: Event): Promise<void> {
    const ev = e as google.maps.places.PlacePredictionSelectEvent;
    this.status = 'Looking up that place...';
    try {
      const { place } = await ev.placePrediction.toPlace().fetchFields({
        fields: [
          'displayName',
          'formattedAddress',
          'location',
          'addressComponents',
          'types',
          'id',
        ],
      });
      this.applyPlace(place);
      this.status = '';
    } catch (err) {
      this.status = `Could not load that place: ${(err as Error).message}`;
    }
  }

  private applyPlace(p: google.maps.places.Place): void {
    const components = p.addressComponents ?? [];
    const long = (type: string) =>
      components.find((c) => c.types.includes(type))?.longText ?? '';
    const short = (type: string) =>
      components.find((c) => c.types.includes(type))?.shortText ?? '';

    const countryCode = short('country');
    const streetNumber = long('street_number');
    const route = long('route');

    // subpremise only means a unit when there is a real street address to be a unit *of*. For a
    // landmark Google puts the locality name there - the Sydney Opera House returns
    // subpremise "Bennelong Point" with no route, which then displayed as Unit: Bennelong Point.
    const isStreetAddress = Boolean(streetNumber || route);
    const unitNumber = isStreetAddress ? long('subpremise') : '';

    this.patch({
      unitNumber: unitNumber || undefined,
      streetNumber: streetNumber || undefined,
      streetName: route || undefined,
      suburb: long('locality') || long('sublocality') || undefined,
      state: short('administrative_area_level_1') || undefined,
      stateFull: long('administrative_area_level_1') || undefined,
      postcode: long('postal_code') || undefined,
      country:
        findCountryByCode(countryCode)?.name || long('country') || undefined,
      countryCode: countryCode || undefined,
      googlePlaceId: p.id ?? undefined,
      formattedAddress: p.formattedAddress ?? undefined,
      placeName: this.placeNameFrom(p, streetNumber, route),
      source: 'GooglePlace',
      components: components.map((c): AddressComponent => ({
        longText: c.longText ?? '',
        shortText: c.shortText ?? '',
        types: [...c.types],
      })),
    });

    const loc = p.location;
    if (loc) {
      this.setCoords(loc.lat(), loc.lng(), 'google');
      this.map?.panTo({ lat: loc.lat(), lng: loc.lng() });
      this.map?.setZoom(18);
    }

    // A fresh pick answers the question this screen asks, so return to the confirmed view.
    this.refiningDetail = false;
  }

  /**
   * A place name is only worth storing when Google matched a *named* place. For an ordinary street
   * address Google returns the address itself as `displayName`, so storing it renders a stutter -
   * "25 Harbour St / 25 Harbour Street" (note it abbreviates, which is why comparing the strings is
   * not enough on its own). The place types say which case this is.
   */
  private placeNameFrom(
    p: google.maps.places.Place,
    streetNumber: string,
    route: string
  ): string | undefined {
    const name = p.displayName?.trim();
    if (!name) return undefined;

    const types = p.types ?? [];
    const isNamedPlace =
      types.includes('establishment') || types.includes('point_of_interest');
    if (!isNamedPlace) return undefined;

    // Belt and braces: a named place whose name simply is its street address adds nothing.
    const street = [streetNumber, route].filter(Boolean).join(' ');
    return street && isRedundantPlaceName(name, street) ? undefined : name;
  }

  private findOnMap = async (): Promise<void> => {
    if (!this.map) {
      this.status = 'The map is still loading - try again in a moment.';
      return;
    }
    const query = [
      this.composeStreet(),
      this.composeLocality(),
      this.val('country'),
    ]
      .map((s) => s.trim())
      .filter(Boolean)
      .join(', ');
    if (!query) {
      this.status = 'Enter an address first.';
      return;
    }
    const token = ++this.resolveToken;
    this.resetResolutionState();
    this.clearFinder();
    this.status = 'Finding that address...';
    try {
      this.geocoder ??= new google.maps.Geocoder();
      const { results } = await this.geocoder.geocode({
        address: query,
        region: this.regionCode(),
      });
      if (token !== this.resolveToken || !this.isConnected) return;
      const best = results[0];
      if (!best) {
        this.manualStage = 'entering';
        this.status = `No match found. Click the map to place the pin where the ${terms.loginSite.lower} is.`;
        return;
      }
      // No `source` change - see locateCurrentAddress. Resolving places the pin; it does not
      // make the typed wording Google's.
      this.patch({
        googlePlaceId: best.place_id ?? undefined,
        formattedAddress: best.formatted_address ?? undefined,
      });
      const loc = best.geometry.location;
      this.setCoords(loc.lat(), loc.lng(), 'google');
      this.map.panTo({ lat: loc.lat(), lng: loc.lng() });
      this.map.setZoom(this.zoomForPrecision(best.geometry.location_type));
      // dropsPlaceName is false: the user asked to locate the address they typed, which says
      // nothing about whether the site's name still applies. R4 allows discarding identity only
      // when it no longer applies, and this cannot know that.
      this.proposeFromResult(best, false);

      // Only a rooftop match is an actual building. Everything else is a road, a road segment or a
      // suburb centroid - close enough to start from, not close enough to route an auditor to.
      const exact = best.geometry.location_type === 'ROOFTOP';
      this.manualStage = exact ? 'exact' : 'approximate';
      this.pinAdjusted = false;
      this.status = '';
    } catch {
      this.status =
        'Address lookup is unavailable - drop a pin on the map instead.';
    }
  };

  private zoomForPrecision(t: string): number {
    if (t === 'ROOFTOP') return 18;
    if (t === 'RANGE_INTERPOLATED') return 17;
    if (t === 'GEOMETRIC_CENTER') return 15;
    return 13; // APPROXIMATE
  }

  private setCoords(
    lat: number,
    lng: number,
    origin: 'google' | 'manual'
  ): void {
    if (origin === 'google') this.mapNote = '';
    this.patch(coordsPatch(lat, lng, origin));
    this.ensureMarker(lat, lng);
    if (origin === 'manual') {
      this.pinAdjusted = true;
      this.clearFinder();
      void this.reverseGeocode(lat, lng);
    }
  }

  /**
   * Names a hand-dropped pin so it is not a bare point. Structured parts are only filled where
   * still empty, so anything typed is never overwritten.
   */
  private async reverseGeocode(lat: number, lng: number): Promise<void> {
    const token = ++this.reverseGeocodeToken;
    this.pinCandidate = null;
    try {
      this.geocoder ??= new google.maps.Geocoder();
      const { results } = await this.geocoder.geocode({
        location: { lat, lng },
      });
      if (token !== this.reverseGeocodeToken || !this.isConnected) return;
      const best = results[0];
      if (!best?.formatted_address) {
        this.mapNote = 'No address found at this pin.';
        return;
      }
      this.mapNote = '';
      this.pinCandidate = {
        formattedAddress: best.formatted_address,
        placeId: best.place_id ?? undefined,
        parts: this.partsFrom(best.address_components ?? []),
        dropsPlaceName: true,
      };
    } catch {
      if (token !== this.reverseGeocodeToken) return;
      this.mapNote = 'Could not look up an address for this pin.';
    }
  }

  /**
   * Offers what Google resolved as the written address. Manual entry is a best guess - typed in a
   * hurry, often without a state or postcode and rarely capitalised the way Google writes it - so
   * every successful resolve proposes the tidied version rather than leaving the fields as typed.
   */
  private proposeFromResult(
    result: google.maps.GeocoderResult,
    dropsPlaceName: boolean
  ): void {
    const exact = result.geometry.location_type === 'ROOFTOP';
    if (!result.formatted_address) return;
    this.pinCandidate = {
      formattedAddress: result.formatted_address,
      placeId: result.place_id ?? undefined,
      parts: this.partsFrom(result.address_components ?? []),
      dropsPlaceName,
    };
    this.adoptIfMoreComplete(exact);
  }

  /**
   * Takes Google's detail without being asked when it is strictly more complete than what we hold.
   *
   * A site saved as "25 harbour st, sydney" against a result of "25 Harbour St, Sydney NSW 2000,
   * Australia" is not a judgement call - the second is the first plus a state, a postcode and a
   * country. Making someone click to accept strictly better information is friction, and it leaves
   * the sparse version on screen looking like the answer.
   *
   * Anything that is *not* strictly more complete stays an offer, because then it is a genuine
   * choice between two versions rather than filling in blanks.
   */
  private adoptIfMoreComplete(exact: boolean): void {
    const candidate = this.pinCandidate;
    if (!candidate) return;

    const { adopt, fillBlanksOnly } = autoAdoptDecision(
      this.address,
      candidate,
      exact
    );
    if (!adopt) return;

    this.detailsAutoFilled = true;
    this.acceptPinCandidate({ keepOffer: true, fillBlanksOnly });
  }

  /**
   * Adopts Google's wording for the pin's location.
   *
   * `source` is deliberately untouched (rule R2): it records how the *pin* was determined, and
   * taking Google's words for a hand-placed pin does not make that pin Google-located.
   *
   * `fillBlanksOnly` is the difference between the editor helping and the editor deciding. When
   * the user clicks Use these details they have chosen this wording, so it applies wholesale. When
   * the editor adopts on its own it may only fill what is empty - journey J3 is a site whose
   * wording AGWA wants left exactly as written, needing nothing but a pin.
   */
  private acceptPinCandidate(options?: {
    keepOffer?: boolean;
    fillBlanksOnly?: boolean;
  }): void {
    const candidate = this.pinCandidate;
    if (!candidate) return;
    if (!options?.keepOffer) {
      this.pinCandidate = null;
    }
    this.clearFinder();

    this.patch(
      proposalPatch(this.address, candidate, {
        fillBlanksOnly: options?.fillBlanksOnly ?? false,
      })
    );
  }

  /** Every structured part Google returns for the pin - the user opted in, so it applies wholesale. */
  private partsFrom(
    components: google.maps.GeocoderAddressComponent[]
  ): AddressTextParts {
    const long = (type: string) =>
      components.find((c) => c.types.includes(type))?.long_name ?? '';
    const short = (type: string) =>
      components.find((c) => c.types.includes(type))?.short_name ?? '';
    const countryCode = short('country');
    const parts: [keyof AddressTextParts, string][] = [
      ['streetNumber', long('street_number')],
      ['streetName', long('route')],
      ['suburb', long('locality') || long('sublocality')],
      ['state', short('administrative_area_level_1')],
      ['stateFull', long('administrative_area_level_1')],
      ['postcode', long('postal_code')],
      ['country', findCountryByCode(countryCode)?.name ?? long('country')],
      ['countryCode', countryCode],
    ];
    const filled: AddressTextParts = {};
    for (const [key, value] of parts) {
      if (value) {
        filled[key] = value;
      }
    }
    return filled;
  }

  private ensureMarker(lat: number, lng: number): void {
    if (!this.map) return;
    if (!this.marker) {
      this.marker = new google.maps.marker.AdvancedMarkerElement({
        map: this.map,
        position: { lat, lng },
        gmpDraggable: true,
      });
      this.marker.addListener('dragend', () => this.readMarker());
    } else {
      this.marker.position = { lat, lng };
    }
  }

  // AdvancedMarkerElement.position may be a LatLng (methods) or LatLngLiteral (numbers).
  private readMarker(): void {
    const p = this.marker?.position;
    if (!p) return;
    const lat = typeof p.lat === 'function' ? p.lat() : p.lat;
    const lng = typeof p.lng === 'function' ? p.lng() : p.lng;
    this.setCoords(lat, lng, 'manual');
  }

  private emitChange(): void {
    this.dispatchEvent(
      new CustomEvent('agwa-address-change', {
        detail: { address: this.address },
        bubbles: true,
        composed: true,
      })
    );
  }

  private teardownMap(): void {
    if (this.marker) this.marker.map = null;
    this.marker = undefined;
    this.map = undefined;
    this.geocoder = undefined;
    this.autocomplete = undefined;
  }

  // Scoped by tag name because this renders in light DOM. Every value is a Shoelace token, so the
  // surface follows the app's theme in both light and dark.
  private styleTemplate() {
    return html`<style>
      agwa-site-address-edit {
        display: block;
      }
      agwa-site-address-edit .addr-grid {
        display: grid;
        grid-template-columns: minmax(320px, 1fr) minmax(320px, 480px);
        gap: var(--sl-spacing-x-large);
        align-items: start;
      }
      @media (max-width: 900px) {
        agwa-site-address-edit .addr-grid {
          grid-template-columns: 1fr;
        }
      }
      agwa-site-address-edit .addr-col {
        min-width: 0;
      }
      agwa-site-address-edit .addr-label {
        display: block;
        font-size: var(--sl-font-size-small);
        font-weight: var(--sl-font-weight-semibold);
        color: var(--sl-color-neutral-700);
        margin-bottom: var(--sl-spacing-2x-small);
      }
      agwa-site-address-edit .addr-hint {
        margin: var(--sl-spacing-2x-small) 0 0;
        font-size: var(--sl-font-size-small);
        color: var(--sl-color-neutral-500);
        line-height: var(--sl-line-height-normal);
      }
      agwa-site-address-edit .addr-note {
        margin: var(--sl-spacing-small) 0 0;
        font-size: var(--sl-font-size-small);
        color: var(--sl-color-neutral-600);
      }
      agwa-site-address-edit .addr-note-warn {
        color: var(--sl-color-warning-700);
      }

      /* The resolved address, rendered as the answer rather than as inputs. */
      agwa-site-address-edit .addr-summary {
        margin-top: var(--sl-spacing-medium);
        padding: var(--sl-spacing-medium);
        border: 1px solid var(--sl-color-neutral-200);
        border-radius: var(--sl-border-radius-medium);
        background: var(--sl-color-neutral-50);
      }
      agwa-site-address-edit .addr-summary-lines {
        display: flex;
        flex-direction: column;
        gap: 2px;
      }
      agwa-site-address-edit .addr-line-lead {
        font-size: var(--sl-font-size-medium);
        font-weight: var(--sl-font-weight-semibold);
        color: var(--sl-color-neutral-900);
        line-height: var(--sl-line-height-dense);
      }
      agwa-site-address-edit .addr-line {
        font-size: var(--sl-font-size-medium);
        color: var(--sl-color-neutral-700);
        line-height: var(--sl-line-height-dense);
      }
      agwa-site-address-edit .addr-summary-foot {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: var(--sl-spacing-small);
        margin-top: var(--sl-spacing-small);
        padding-top: var(--sl-spacing-small);
        border-top: 1px solid var(--sl-color-neutral-200);
      }
      agwa-site-address-edit .addr-link {
        background: none;
        border: none;
        padding: 0;
        font-size: var(--sl-font-size-small);
        color: var(--sl-color-primary-600);
        cursor: pointer;
        text-decoration: none;
      }
      agwa-site-address-edit .addr-link:hover {
        text-decoration: underline;
      }
      agwa-site-address-edit .addr-link:focus-visible {
        outline: var(--sl-focus-ring);
        outline-offset: 2px;
        border-radius: var(--sl-border-radius-small);
      }

      agwa-site-address-edit .addr-current {
        display: flex;
        flex-direction: column;
        gap: 2px;
        margin-bottom: var(--sl-spacing-large);
        padding-bottom: var(--sl-spacing-medium);
        border-bottom: 1px solid var(--sl-color-neutral-200);
      }
      agwa-site-address-edit .addr-current-head {
        display: flex;
        align-items: center;
        gap: var(--sl-spacing-x-small);
      }
      agwa-site-address-edit .addr-badge {
        font-size: var(--sl-font-size-2x-small);
        font-weight: var(--sl-font-weight-semibold);
        text-transform: uppercase;
        letter-spacing: 0.04em;
        padding: 1px var(--sl-spacing-x-small);
        border-radius: var(--sl-border-radius-pill);
        border: 1px solid transparent;
      }
      agwa-site-address-edit .addr-badge.verified {
        background: var(--sl-color-success-50);
        color: var(--sl-color-success-800);
        border-color: var(--sl-color-success-200);
      }
      agwa-site-address-edit .addr-badge.uncertain {
        background: var(--sl-color-warning-50);
        color: var(--sl-color-warning-800);
        border-color: var(--sl-color-warning-200);
      }
      agwa-site-address-edit .addr-current-label {
        font-size: var(--sl-font-size-x-small);
        font-weight: var(--sl-font-weight-semibold);
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: var(--sl-color-neutral-500);
      }
      agwa-site-address-edit .addr-current-value {
        font-size: var(--sl-font-size-medium);
        color: var(--sl-color-neutral-900);
      }
      agwa-site-address-edit .addr-current .addr-link {
        align-self: flex-start;
        margin-top: var(--sl-spacing-3x-small);
      }
      agwa-site-address-edit .is-hidden {
        display: none;
      }
      agwa-site-address-edit .addr-mode-row {
        display: flex;
        align-items: center;
        gap: var(--sl-spacing-small);
        margin-bottom: var(--sl-spacing-medium);
      }
      agwa-site-address-edit .addr-help-btn {
        width: 1.35rem;
        height: 1.35rem;
        border-radius: 50%;
        border: solid var(--sl-input-border-width) var(--sl-color-neutral-300);
        background: transparent;
        color: var(--sl-color-neutral-600);
        font-size: var(--sl-font-size-x-small);
        font-weight: var(--sl-font-weight-bold);
        line-height: 1;
        cursor: pointer;
        padding: 0;
      }
      agwa-site-address-edit .addr-help-btn:hover {
        border-color: var(--sl-color-primary-600);
        color: var(--sl-color-primary-600);
      }
      agwa-site-address-edit .addr-help-btn:focus-visible {
        outline: var(--sl-focus-ring);
        outline-offset: 2px;
      }
      agwa-site-address-edit .addr-help p {
        margin: 0 0 var(--sl-spacing-x-small);
        max-width: 34ch;
        text-align: left;
      }
      agwa-site-address-edit .addr-help p:last-child {
        margin-bottom: 0;
      }
      agwa-site-address-edit .addr-modes {
        display: inline-flex;
        gap: 0;
        border: solid var(--sl-input-border-width) var(--sl-input-border-color);
        border-radius: var(--sl-input-border-radius-medium);
        overflow: hidden;
      }
      agwa-site-address-edit .addr-tab {
        border: none;
        background: transparent;
        color: var(--sl-color-neutral-700);
        padding: 0 var(--sl-spacing-medium);
        height: var(--sl-input-height-small);
        font-size: var(--sl-font-size-small);
        font-weight: var(--sl-font-weight-semibold);
        cursor: pointer;
      }
      agwa-site-address-edit .addr-tab + .addr-tab {
        border-left: solid var(--sl-input-border-width)
          var(--sl-input-border-color);
      }
      agwa-site-address-edit .addr-tab.active {
        background: var(--sl-color-primary-600);
        color: var(--sl-color-neutral-0);
      }
      agwa-site-address-edit .addr-tab:focus-visible {
        outline: var(--sl-focus-ring);
        outline-offset: -2px;
      }
      agwa-site-address-edit .addr-stage {
        margin: var(--sl-spacing-small) 0 0;
        padding: var(--sl-spacing-x-small) var(--sl-spacing-small);
        border-radius: var(--sl-border-radius-medium);
        font-size: var(--sl-font-size-small);
        line-height: var(--sl-line-height-normal);
      }
      agwa-site-address-edit .addr-stage-todo {
        background: var(--sl-color-warning-50);
        color: var(--sl-color-warning-800);
        border: 1px solid var(--sl-color-warning-200);
      }
      agwa-site-address-edit .addr-stage-ok {
        background: var(--sl-color-success-50);
        color: var(--sl-color-success-800);
        border: 1px solid var(--sl-color-success-200);
      }
      agwa-site-address-edit .addr-manual-lead {
        margin-top: 0;
        margin-bottom: var(--sl-spacing-x-small);
      }
      agwa-site-address-edit .addr-unit {
        margin-top: var(--sl-spacing-medium);
        max-width: 260px;
      }
      agwa-site-address-edit .addr-fields {
        display: grid;
        grid-template-columns: repeat(12, 1fr);
        gap: var(--sl-spacing-small);
        margin-top: var(--sl-spacing-medium);
      }
      agwa-site-address-edit .span-3 {
        grid-column: span 3;
      }
      agwa-site-address-edit .span-4 {
        grid-column: span 4;
      }
      agwa-site-address-edit .span-6 {
        grid-column: span 6;
      }
      agwa-site-address-edit .span-8 {
        grid-column: span 8;
      }
      agwa-site-address-edit .span-12 {
        grid-column: span 12;
      }
      @media (max-width: 560px) {
        agwa-site-address-edit .addr-fields > * {
          grid-column: span 12 !important;
        }
      }
      agwa-site-address-edit .addr-field {
        display: flex;
        flex-direction: column;
        min-width: 0;
      }
      agwa-site-address-edit .addr-field label {
        font-size: var(--sl-font-size-small);
        color: var(--sl-color-neutral-700);
        margin-bottom: var(--sl-spacing-3x-small);
      }
      agwa-site-address-edit .addr-field input,
      agwa-site-address-edit .addr-field select {
        border: solid var(--sl-input-border-width) var(--sl-input-border-color);
        border-radius: var(--sl-input-border-radius-medium);
        padding: 0 var(--sl-input-spacing-medium);
        height: var(--sl-input-height-medium);
        font-size: var(--sl-input-font-size-medium);
        font-family: var(--sl-input-font-family);
        background: var(--sl-input-background-color);
        color: var(--sl-input-color);
        width: 100%;
        box-sizing: border-box;
      }
      agwa-site-address-edit .addr-field input:focus-visible,
      agwa-site-address-edit .addr-field select:focus-visible {
        outline: var(--sl-focus-ring);
        outline-offset: var(--sl-focus-ring-offset);
        border-color: var(--sl-input-border-color-focus);
      }
      agwa-site-address-edit .addr-field input::placeholder {
        color: var(--sl-input-placeholder-color);
      }

      agwa-site-address-edit .addr-actions {
        display: flex;
        align-items: center;
        gap: var(--sl-spacing-small);
        margin-top: var(--sl-spacing-medium);
        flex-wrap: wrap;
      }
      agwa-site-address-edit .addr-btn {
        border: solid var(--sl-input-border-width) var(--sl-color-primary-600);
        background: transparent;
        color: var(--sl-color-primary-600);
        border-radius: var(--sl-input-border-radius-medium);
        padding: 0 var(--sl-spacing-medium);
        height: var(--sl-input-height-small);
        font-size: var(--sl-font-size-small);
        font-weight: var(--sl-font-weight-semibold);
        cursor: pointer;
      }
      agwa-site-address-edit .addr-btn:hover {
        background: var(--sl-color-primary-50);
      }
      agwa-site-address-edit .addr-btn:focus-visible {
        outline: var(--sl-focus-ring);
        outline-offset: var(--sl-focus-ring-offset);
      }

      agwa-site-address-edit .addr-map-head {
        display: flex;
        align-items: baseline;
        justify-content: space-between;
        gap: var(--sl-spacing-small);
      }
      agwa-site-address-edit .addr-map-wrap {
        position: relative;
      }
      agwa-site-address-edit .addr-map {
        width: 100%;
        height: 340px;
        border-radius: var(--sl-border-radius-medium);
        border: 1px solid var(--sl-color-neutral-200);
        overflow: hidden;
        background: var(--sl-color-neutral-100);
      }
      agwa-site-address-edit .addr-map-status {
        position: absolute;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: var(--sl-spacing-medium);
        text-align: center;
        font-size: var(--sl-font-size-small);
        color: var(--sl-color-neutral-600);
      }
      agwa-site-address-edit .addr-pin-found {
        display: flex;
        flex-direction: column;
        gap: 2px;
        margin-top: var(--sl-spacing-small);
        padding: var(--sl-spacing-x-small) var(--sl-spacing-small);
        border: 1px solid var(--sl-color-neutral-200);
        border-radius: var(--sl-border-radius-medium);
        background: var(--sl-color-neutral-50);
      }
      agwa-site-address-edit .addr-pin-found-label {
        font-size: var(--sl-font-size-x-small);
        font-weight: var(--sl-font-weight-semibold);
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: var(--sl-color-neutral-500);
      }
      agwa-site-address-edit .addr-pin-found-value {
        font-size: var(--sl-font-size-small);
        color: var(--sl-color-neutral-800);
      }
      agwa-site-address-edit .addr-pin-found .addr-link {
        align-self: flex-start;
        margin-top: 2px;
      }
      agwa-site-address-edit .addr-coords {
        margin-top: var(--sl-spacing-x-small);
        font-family: var(--sl-font-mono);
        font-size: var(--sl-font-size-x-small);
        color: var(--sl-color-neutral-500);
      }
    </style>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'agwa-site-address-edit': AgwaSiteAddressEdit;
  }
}
